/**
 * Which build is this — the question "which APK is on my phone?" has a correct answer only the
 * shell can give.
 *
 * There are two sources and they are not interchangeable. The bundle carries the version it was
 * COMPILED from, inlined by Vite from `mobile-version.json`; `@capacitor/app` reports the version
 * ANDROID ACTUALLY INSTALLED, read off the package manager. Those differ in exactly the case this
 * exists for: a remote-URL (`CAP_ENV=beta`) shell serves a freshly deployed web build inside an
 * older binary, so the bundle's number describes the page and the package's number describes the
 * app. The installed one wins on native because the reason to display this at all is to know
 * which download is on the device.
 *
 * Safe to call in a browser: `isNativePlatform()` is false there, no Capacitor package is
 * touched, and the compiled-in constants are returned. Same rule the rest of `src/native` is
 * under — nothing here may make the web build depend on a shell.
 */

import { App } from '@capacitor/app';
import { isNativePlatform } from './platform';

export interface AppVersion {
  /** Player-facing version, e.g. `0.1.0`. */
  readonly version: string;
  /** The monotonic build number both stores compare on, and what names one APK against another. */
  readonly build: number;
  /**
   * The commit this bundle was built from, or null where it could not be known.
   *
   * **The web's identity, where `build` is the store's.** `build` is raised only by the Android
   * workflow, so a web deploy never moves it and every web build reports the committed floor —
   * which is how a live site sat on "build 2" through fifteen APK releases. A monotonic counter is
   * the right identity for a store artifact, where two uploads have to be orderable; for a web
   * deploy the question anybody actually asks is "is the fix on there?", and only a commit answers
   * that. See `build-version.ts` for where it comes from and why the production host cannot read
   * git itself.
   */
  readonly commit: string | null;
  /** True when this came from the installed package rather than from the compiled-in constants. */
  readonly installed: boolean;
}

/** What the bundle was compiled from. Always available, on every platform. */
export const BUILT_VERSION: AppVersion = {
  version: __APP_VERSION__,
  build: __APP_BUILD__,
  commit: __APP_COMMIT__,
  installed: false,
};

/**
 * Format for display — one string, so every surface renders it the same way.
 *
 * **One shape on every surface: a version and a build number.** A sha was tried here and was
 * wrong — it is an identifier for a developer, not a version for a player, and "which build am I
 * on" is a question a number answers and a hash does not. Both surfaces now carry a number that
 * increments on its own: the installed app's comes from the package manager (the store's own
 * counter), and the page's is the commit count of the history it was built from. The commit is
 * still inlined and is on the element's `title`, where somebody debugging can find it and nobody
 * else has to read it.
 */
export function formatAppVersion(v: AppVersion): string {
  return `v${v.version} (build ${v.build})`;
}

/**
 * The version to show. On native this is the installed package; everywhere else, and whenever the
 * shell cannot answer, it is what the bundle was built from.
 *
 * A failure here is never surfaced: not knowing the build number is not a reason to put an error
 * in front of somebody, and the compiled-in value is a true statement about the page either way.
 */
export async function resolveAppVersion(): Promise<AppVersion> {
  if (!isNativePlatform()) return BUILT_VERSION;
  try {
    const info = await App.getInfo();
    const build = Number.parseInt(info.build, 10);
    return {
      version: info.version || BUILT_VERSION.version,
      build: Number.isFinite(build) ? build : BUILT_VERSION.build,
      // The commit the *bundle* was compiled from either way: the package manager knows nothing
      // about it, and on a remote-URL beta shell it is the one field that describes the page
      // rather than the binary around it.
      commit: BUILT_VERSION.commit,
      installed: true,
    };
  } catch {
    return BUILT_VERSION;
  }
}
